import { GenericCommentStream } from "components/Comments/GenericCommentStream";
import { gql } from "generated-graphql";
import { CommentThreadFragment, SubmissionCommentStreamFragment } from "generated-graphql/graphql";
import { useCreateSubmissionComment, useDeleteSubmissionComment } from "hooks/comments";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * Example usage below, delete this once we have a real example
 */
// import { useQuery } from "@apollo/client";
// import { Box } from "@mui/system";
// import { gql } from "generated-graphql";
// import { useParams } from "react-router";
// import { SubmissionCommentStream } from "routes/Customer/SubmissionCommentStream";

// export const GET_SUBMISSION = gql(`
//   query GetSubmission($id: ID!) {
//     submission(id: $id) {
//       id
//       comments {
//         id
//         comment
//         isSystemMessage
//         user {
//             id
//             email
//             person {
//                 first
//                 last
//             }
//         }
//         createdAt
//         updatedAt
//       }
//     }
//   }
// `);

// export function SubmissionCommentsTest() {
//   const { submissionId } = useParams();
//   const { data, loading, error } = useQuery(GET_SUBMISSION, {
//     variables: { id: submissionId ?? "" },
//     skip: !submissionId,
//   });
//   return (
//     <div>
//       {!loading && !error && data && data.submission && (
//         <Box sx={{ width: "500px", ml: "10px" }}>
//           <SubmissionCommentStream submission={data.submission} />
//         </Box>
//       )}
//     </div>
//   );
// }

gql(`
  fragment SubmissionCommentStream on Submission {
    id
    comments {
      ...CommentThread
    }
  }
`);

type SubmissionType = SubmissionCommentStreamFragment;

interface SubmissionCommentStreamProps {
  submissionId?: string | null | undefined;
  comments?: SubmissionType["comments"] | null | undefined;
  commentAdded?: (comment: CommentThreadFragment) => void;
  commentDeleted?: (commentId: string) => void;
  /** Title for the comment section */
  title?: string;
}

export const SubmissionCommentStream = ({
  submissionId,
  comments,
  commentAdded,
  commentDeleted,
  title = "Comments",
}: SubmissionCommentStreamProps) => {
  const { user: currentUser } = useCurrentUser();
  const { addComment, loading: addLoading } = useCreateSubmissionComment();
  const { deleteComment, loading: deleteLoading } = useDeleteSubmissionComment();

  const handleAddComment = useCallback(
    async (message: string) => {
      // we're only going to roundtrip the comment if we're in edit mode
      if (submissionId) {
        const result = await addComment(submissionId ?? "", message);
        const newComment = result?.data?.upsertComment;
        if (newComment) {
          commentAdded?.(newComment);
        }
      } else {
        const newComment = {
          id: uuidv4(),
          comment: message,
          userId: currentUser?.id || "",
          createdAt: new Date(),
          isSystemMessage: false,
          // this looks heavy handed, but we need these to display the comment
          // this is necessary for submissions that haven't been created yet since we cant
          // round trip the comment
          user: {
            id: currentUser?.id || "",
            email: currentUser?.email || "",
            person: currentUser?.person
              ? {
                  first: currentUser.person.first,
                  last: currentUser.person.last,
                }
              : null,
          },
        };

        commentAdded?.(newComment as CommentThreadFragment);
      }
    },
    [addComment, submissionId, commentAdded, currentUser]
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      commentDeleted?.(commentId);

      // we're only going to roundtrip *delete* the comment if we're in edit mode
      // if we're in create it doesn't exist yet
      if (submissionId) {
        await deleteComment(commentId);
      }
    },
    [deleteComment, commentDeleted, submissionId]
  );

  return (
    <GenericCommentStream
      title={title}
      comments={comments ?? []}
      currentUserId={currentUser?.id}
      onAddComment={handleAddComment}
      onDeleteComment={handleDeleteComment}
      addLoading={addLoading}
      deleteLoading={deleteLoading}
      placeholder="Leave a comment"
      buttonText="Submit"
      emptyStateMessage=""
      showDeleteButton={true}
      inputMaxRows={12}
    />
  );
};
